import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import {
  CognitoUserPool,
  CognitoUser,
  CognitoUserSession,
  CognitoRefreshToken,
} from 'amazon-cognito-identity-js';
import { environment } from '../../environments/environment';
import { Session } from './models';

/**
 * TokenService manages the lifecycle of Cognito authentication tokens.
 *
 * Tokens are stored in private class fields (NOT localStorage) to reduce
 * XSS attack surface. The service schedules proactive token refresh
 * 5 minutes before access token expiry.
 *
 * Requirements: 9.1, 9.2, 9.3
 */
@Injectable({ providedIn: 'root' })
export class TokenService {
  private accessToken: string | null = null;
  private idToken: string | null = null;
  private refreshToken: string | null = null;
  private expiresAt: number | null = null;

  private refreshTimerId: ReturnType<typeof setTimeout> | null = null;

  private readonly accessTokenSubject = new BehaviorSubject<string | null>(null);

  /** Observable stream of the current access token (null when unauthenticated). */
  readonly accessToken$: Observable<string | null> = this.accessTokenSubject.asObservable();

  private readonly userPool = new CognitoUserPool({
    UserPoolId: environment.cognitoUserPoolId,
    ClientId: environment.cognitoClientId,
  });

  /**
   * Store a new set of tokens and schedule proactive refresh.
   * Parses the access token JWT payload to derive expiry if not provided.
   */
  setTokens(session: Session): void {
    this.accessToken = session.accessToken;
    this.idToken = session.idToken;
    this.refreshToken = session.refreshToken;
    this.expiresAt = session.expiresAt || this.parseExpiry(session.accessToken);

    this.accessTokenSubject.next(this.accessToken);
    this.scheduleRefresh();
  }

  /**
   * Clear all tokens and cancel any pending refresh.
   * Emits null on the accessToken$ stream.
   */
  clearTokens(): void {
    this.accessToken = null;
    this.idToken = null;
    this.refreshToken = null;
    this.expiresAt = null;

    this.cancelScheduledRefresh();
    this.accessTokenSubject.next(null);
  }

  /**
   * Synchronously return the current access token (or null).
   */
  getAccessToken(): string | null {
    return this.accessToken;
  }

  /**
   * Returns true if a non-expired access token is currently held.
   */
  isAuthenticated(): boolean {
    if (!this.accessToken || !this.expiresAt) {
      return false;
    }
    return Date.now() < this.expiresAt;
  }

  /**
   * Schedule a token refresh 5 minutes (300 000 ms) before the access token expires.
   * If the token expires in less than 5 minutes, refresh immediately.
   */
  private scheduleRefresh(): void {
    this.cancelScheduledRefresh();

    if (!this.expiresAt) {
      return;
    }

    const FIVE_MINUTES_MS = 5 * 60 * 1000;
    const now = Date.now();
    const timeUntilExpiry = this.expiresAt - now;
    const delay = Math.max(timeUntilExpiry - FIVE_MINUTES_MS, 0);

    this.refreshTimerId = setTimeout(() => {
      this.performRefresh();
    }, delay);
  }

  /**
   * Use the stored refresh token to obtain new tokens from Cognito.
   * On failure, clears all tokens (triggers logout state).
   */
  private performRefresh(): void {
    if (!this.refreshToken) {
      this.clearTokens();
      return;
    }

    const cognitoRefreshToken = new CognitoRefreshToken({
      RefreshToken: this.refreshToken,
    });

    // We need a CognitoUser to call refreshSession.
    // Use a placeholder username — Cognito resolves the user from the refresh token.
    const cognitoUser = new CognitoUser({
      Username: 'TOKEN_REFRESH_USER',
      Pool: this.userPool,
    });

    cognitoUser.refreshSession(
      cognitoRefreshToken,
      (err: Error | null, session: CognitoUserSession | null) => {
        if (err || !session) {
          this.clearTokens();
          return;
        }

        const newSession: Session = {
          accessToken: session.getAccessToken().getJwtToken(),
          idToken: session.getIdToken().getJwtToken(),
          refreshToken: this.refreshToken!, // Refresh token doesn't rotate by default
          expiresAt: session.getAccessToken().getExpiration() * 1000,
        };

        this.setTokens(newSession);
      }
    );
  }

  /**
   * Parse the `exp` claim from a JWT access token by base64-decoding the payload segment.
   * Returns expiry as Unix timestamp in milliseconds, or null if parsing fails.
   */
  parseExpiry(token: string): number | null {
    try {
      const parts = token.split('.');
      if (parts.length !== 3) {
        return null;
      }
      // Base64url decode the payload (middle segment)
      const payload = parts[1];
      const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
      const jsonStr = atob(base64);
      const claims = JSON.parse(jsonStr);
      if (typeof claims.exp === 'number') {
        // `exp` is in seconds; convert to milliseconds
        return claims.exp * 1000;
      }
      return null;
    } catch {
      return null;
    }
  }

  /**
   * Cancel any pending refresh timer.
   */
  private cancelScheduledRefresh(): void {
    if (this.refreshTimerId !== null) {
      clearTimeout(this.refreshTimerId);
      this.refreshTimerId = null;
    }
  }
}
